import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { companyInfo } from '../../utils/dataInfo';
import { formatNumberWithComma, getCategoriesNameById } from '../../utils/helpers';

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
    const words = text.split(' ');
    const lines = [];
    let currentLine = words[0];

    for (let i = 1; i < words.length; i++) {
        const word = words[i];
        const width = doc.getTextWidth(currentLine + ' ' + word);
        if (width < maxWidth) {
            currentLine += ' ' + word;
        } else {
            lines.push(currentLine);
            currentLine = word;
        }
    }
    lines.push(currentLine);
    return lines;
};

// Manual table drawing function for PDF (using landscape mode)
const drawStockTable = (doc, data, startY, branchNames) => {
    const pageWidth = doc.internal.pageSize.width; // This will be longer in landscape
    const pageHeight = doc.internal.pageSize.height; // This will be shorter in landscape
    const margin = 20; // Increased margin for better spacing in landscape
    const rowHeight = 12;

    // Column widths for landscape mode - only selected columns
    const colWidths = [25, 50, 25, 25, 25, 30, 20, 30]; // Stock Date, Item Name, Balance, Cost, Selling, Stock Level, Status, Branch
    
    let currentY = startY;
    const neededHeight = (data.length + 1) * rowHeight;

    // Check if we need a new page (using pageHeight for landscape)
    if (currentY + neededHeight > pageHeight - 30) {
        doc.addPage('l'); // Add landscape page
        currentY = margin;
    }

    // Draw table header
    doc.setFillColor(25, 118, 210); // Blue header
    doc.setTextColor(255, 255, 255); // White text
    doc.setFont(undefined, 'bold');

    const headers = ['Stock Date', 'Item Name', 'Balance', 'Cost', 'Selling', 'Stock Level', 'Status', 'Branch'];
    let xPos = margin;

    // Draw header background
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

    // Draw header text
    headers.forEach((header, index) => {
        const cellX = xPos + 2;
        doc.text(header, cellX, currentY + 7);
        xPos += colWidths[index];
    });

    currentY += rowHeight;

    // Draw table rows
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0); // Black text

    data.forEach((row, rowIndex) => {
        // Calculate needed height for this row
        let maxLines = 1;
        const cellLines = row.map((cell, cellIndex) => {
            const text = String(cell);
            const maxWidth = colWidths[cellIndex] - 4;
            const lines = splitTextIntoLines(doc, text, maxWidth);
            maxLines = Math.max(maxLines, lines.length);
            return lines;
        });

        const actualRowHeight = rowHeight * maxLines;

        // Check for page break (using pageHeight for landscape)
        if (currentY + actualRowHeight > pageHeight - 30) {
            doc.addPage('l'); // Add landscape page
            currentY = margin;
            
            // Redraw header on new page
            doc.setFillColor(25, 118, 210);
            doc.setTextColor(255, 255, 255);
            doc.setFont(undefined, 'bold');
            
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            xPos = margin;
            headers.forEach((header, index) => {
                doc.text(header, xPos + 2, currentY + 7);
                xPos += colWidths[index];
            });
            
            currentY += rowHeight;
            doc.setFont(undefined, 'normal');
            doc.setTextColor(0, 0, 0);
        }
        
        // Alternate row colors
        if (rowIndex % 2 === 0) {
            doc.setFillColor(248, 248, 248); // Light gray
        } else {
            doc.setFillColor(255, 255, 255); // White
        }
        
        // Draw row background
        doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
        
        // Draw cell borders
        doc.setDrawColor(200, 200, 200);
        doc.setLineWidth(0.1);
        
        // Draw vertical lines
        let verticalX = margin;
        for (let i = 0; i <= colWidths.length; i++) {
            doc.line(verticalX, currentY, verticalX, currentY + actualRowHeight);
            if (i < colWidths.length) verticalX += colWidths[i];
        }
        
        // Draw horizontal lines
        doc.line(margin, currentY, pageWidth - margin, currentY);
        doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
        
        // Draw row text with multi-line support
        cellLines.forEach((lines, cellIndex) => {
            xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
            
            lines.forEach((line, lineIndex) => {
                const lineY = currentY + 7 + (lineIndex * rowHeight);
                doc.text(line, xPos + 2, lineY);
            });
        });
        
        currentY += actualRowHeight;
    });

    return currentY;
};

// Helper function to get branch name
const getBranchName = (branchId, branchNames) => {
if (!branchNames || !Array.isArray(branchNames)) return 'N/A';
const branch = branchNames.find(b => parseInt(b.id) === parseInt(branchId));
return branch ? branch.name : 'N/A';
};


// Helper function to get stock level
const getStockLevel = (balance) => {
    if (balance <= 0) return 'Out of Stock';
    if (balance <= 10) return 'Low';
    if (balance <= 50) return 'Normal';
    return 'High';
};

// Export stock taking to PDF
export const exportStockTakingPDF = (stocks, branchNames = []) => {
    try {
        const doc = new jsPDF();
        
        // Set document properties
        doc.setProperties({
            title: 'Stock Taking Report',
            subject: 'Stock Taking Form',
            author: companyInfo.name,
            keywords: 'stock, taking, inventory, count',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('STOCK TAKING FORM', 105, 25, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.setTextColor(80, 80, 80);
        let currentY = 35;
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
        currentY += 6;
        doc.text(`Total Items: ${stocks.length}`, 14, currentY);
        currentY += 6;
        
        // Instructions
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text('Instructions:', 14, currentY + 10);
        doc.text('1. Fill in the "Count Figure" column with actual counted quantity', 20, currentY + 20);
        doc.text('2. Note any discrepancies in the "Remarks" column', 20, currentY + 30);
        doc.text('3. Sign and date the completed form', 20, currentY + 40);
        
        currentY += 50;
        
        // Draw the stock taking table
        const pageWidth = doc.internal.pageSize.width;
        const margin = 14;
        const rowHeight = 12;

        // Column widths for stock taking
        const colWidths = [10, 60, 40, 40, 40]; // #, Stock Name, Balance/Quantity, Count Figure, Remarks

        // Check if we need a new page
        let neededHeight = (stocks.length + 1) * rowHeight;
        if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
            doc.addPage();
            currentY = margin;
        }

        // Draw table header
        doc.setFillColor(255, 255, 255);
        doc.setTextColor(25, 118, 210);
        doc.setFont(undefined, 'bold');

        const headers = ['#', 'Stock Name', 'Balance/Quantity', 'Count Figure', 'Remarks'];
        let xPos = margin;

        // Draw header background
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

        // Draw header text
        headers.forEach((header, index) => {
            const cellX = xPos + 2;
            doc.text(header, cellX, currentY + 7);
            xPos += colWidths[index];
        });

        // Draw header underline
        doc.setDrawColor(25, 118, 210);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

        currentY += rowHeight;

        // Draw table rows
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);

        stocks.forEach((stock, rowIndex) => {
            // Check for page break
            if (currentY + rowHeight > doc.internal.pageSize.height - 20) {
                doc.addPage();
                currentY = margin;
                
                // Redraw header on new page
                doc.setFillColor(255, 255, 255);
                doc.setTextColor(25, 118, 210);
                doc.setFont(undefined, 'bold');
                
                doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
                
                xPos = margin;
                headers.forEach((header, index) => {
                    doc.text(header, xPos + 2, currentY + 7);
                    xPos += colWidths[index];
                });
                
                doc.setDrawColor(25, 118, 210);
                doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
                
                currentY += rowHeight;
                doc.setFont(undefined, 'normal');
                doc.setTextColor(0, 0, 0);
            }
            
            // Alternate row colors
            if (rowIndex % 2 === 0) {
                doc.setFillColor(248, 248, 248);
            } else {
                doc.setFillColor(255, 255, 255);
            }
            
            // Draw row background
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            // Draw row data
            const rowData = [
                (rowIndex + 1).toString(),
                stock.name,
                `${stock.balance} ${stock.uom}`,
                '', // Empty for count figure
                ''  // Empty for remarks
            ];
            
            xPos = margin;
            rowData.forEach((cell, cellIndex) => {
                doc.text(cell, xPos + 2, currentY + 7);
                xPos += colWidths[cellIndex];
            });
            
            // Draw row underline
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
        });

        // Add signature section at the end
        const finalY = doc.internal.pageSize.height - 40;
        doc.setFontSize(10);
        doc.setTextColor(0, 0, 0);
        
        // Signature lines
        doc.line(30, finalY, 80, finalY);
        doc.text('Counter Staff Signature', 30, finalY + 10);
        
        doc.line(110, finalY, 160, finalY);
        doc.text('Supervisor Signature', 110, finalY + 10);
        
        doc.line(190, finalY, 240, finalY);
        doc.text('Date', 190, finalY + 10);
        
        const fileName = `stock_taking_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Stock taking PDF export failed:', error);
        throw new Error('Failed to export stock taking PDF: ' + error.message);
    }
};

// Client-side Excel export for Stocks with all attributes
export const exportStocksToExcel = (stocks, branchNames, filters = {}) => {
    try {
        // Prepare data for Excel with all attributes
        const excelData = stocks.map((stock, index) => ({
            'No.': index + 1,
            'ID': stock.id,
            'Stock Date': new Date(stock.stock_date).toLocaleDateString(),
            'Item Name': stock.name,
            'UOM': stock.uom,
            'Initial Quantity': stock.quantity,
            'Balance': stock.balance,
            'Cost Price': formatNumberWithComma(stock.cost),
            'Selling Price': formatNumberWithComma(stock.selling),
            'Total Cost': formatNumberWithComma(stock.total_cost),
            'Total Selling': formatNumberWithComma(stock.total_selling),
            'Stock Level': getStockLevel(stock.balance),
            'Status': stock.status === 1 ? 'Active' : 'Inactive',
            'Branch': getBranchName(stock.branch_id, branchNames),
            'Description': stock.description || '',
            'Notes': stock.notes || '',
            'Created Date': new Date(stock.created_at).toLocaleString(),
            'Updated Date': new Date(stock.updated_at).toLocaleString()
        }));

        // Create workbook and worksheet
        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.json_to_sheet(excelData);

        // Auto-size columns
        const maxWidth = excelData.reduce((widths, row) => {
            Object.keys(row).forEach((key, idx) => {
                const length = String(row[key]).length;
                if (!widths[idx] || length > widths[idx]) {
                    widths[idx] = length;
                }
            });
            return widths;
        }, []);
        
        worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Stocks');

        // Generate Excel file and download
        const fileName = `stocks_export_${new Date().toISOString().split('T')[0]}.xlsx`;
        XLSX.writeFile(workbook, fileName);

        return { success: true, fileName };
    } catch (error) {
        console.error('Stocks Excel export failed:', error);
        throw new Error('Failed to export to Excel: ' + error.message);
    }
};

// Enhanced PDF export for Stocks with selected columns only, using landscape mode
export const exportStocksToPDF = (stocks, branchNames, filters = {}) => {
    return new Promise((resolve, reject) => {
        try {
            // Create PDF in landscape mode
            const doc = new jsPDF({
                orientation: 'landscape',
                unit: 'mm',
                format: 'a4'
            });
            
            const pageWidth = doc.internal.pageSize.getWidth(); // This will be ~297mm in landscape
            const pageHeight = doc.internal.pageSize.getHeight(); // This will be ~210mm in landscape
            const margin = 20;
            let currentY = margin;
            
            // Title
            doc.setFontSize(20);
            doc.setTextColor(25, 118, 210);
            doc.text("Stocks Report", pageWidth / 2, currentY, { align: 'center' });
            currentY += 15;
            
            // Date and time
            doc.setFontSize(10);
            doc.setTextColor(80, 80, 80);
            doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, currentY);
            currentY += 8;
            
            // Summary
            doc.setFontSize(11);
            doc.setTextColor(60, 60, 60);
            doc.text(`Total Items: ${stocks.length}`, margin, currentY);
            currentY += 8;
            
            // Apply filters text
            const filterTexts = [];
            if (filters.stock_level) filterTexts.push(`Stock Level: ${filters.stock_level}`);
            if (filters.status) filterTexts.push(`Status: ${filters.status === '1' ? 'Active' : 'Inactive'}`);
            if (filters.dateFrom) filterTexts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
            if (filters.dateTo) filterTexts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
            
            if (filterTexts.length > 0) {
                doc.setFontSize(9);
                doc.setTextColor(80, 80, 80);
                const filtersText = `Filters: ${filterTexts.join(', ')}`;
                const filtersLines = splitTextIntoLines(doc, filtersText, pageWidth - 2 * margin);
                
                filtersLines.forEach(line => {
                    if (currentY > pageHeight - 40) { // Using pageHeight for landscape check
                        doc.addPage('l'); // Add landscape page
                        currentY = margin;
                    }
                    doc.text(line, margin, currentY);
                    currentY += 6;
                });
                
                currentY += 10;
            }
            
            // Prepare table data with selected columns only
            const tableData = stocks.map(stock => [
                new Date(stock.stock_date).toLocaleDateString(),
                stock.name,
                formatNumberWithComma(stock.balance),
                formatNumberWithComma(stock.cost),
                formatNumberWithComma(stock.selling),
                getStockLevel(stock.balance),
                stock.status === 1 ? 'Active' : 'Inactive',
                getBranchName(stock.branch_id, branchNames)
            ]);
            
            // Draw the table
            drawStockTable(doc, tableData, currentY + 10, branchNames);
            
            // Add page numbers
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(150, 150, 150);
                doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
            }
            
            const fileName = `stocks_report_${new Date().toISOString().split('T')[0]}.pdf`;
            doc.save(fileName);
            resolve();
        } catch (error) {
            reject(error);
        }
    });
};

// Simple CSV export for Stocks with all attributes
export const exportStocksToCSV = (stocks, branchNames) => {
    try {
        // CSV headers with all attributes
        const headers = [
            'ID', 'Stock Date', 'Item Name', 'UOM', 'Initial Quantity', 'Balance', 
            'Cost Price', 'Selling Price', 'Total Cost', 'Total Selling', 
            'Stock Level', 'Status', 'Branch', 'Description', 'Notes', 
            'Created Date', 'Updated Date'
        ];
        
        // Prepare CSV data with all attributes
        const csvData = stocks.map(stock => [
            stock.id,
            new Date(stock.stock_date).toLocaleDateString(),
            `"${stock.name.replace(/"/g, '""')}"`,
            `"${stock.uom.replace(/"/g, '""')}"`,
            stock.quantity,
            stock.balance,
            formatNumberWithComma(stock.cost),
            formatNumberWithComma(stock.selling),
            formatNumberWithComma(stock.total_cost),
            formatNumberWithComma(stock.total_selling),
            getStockLevel(stock.balance),
            stock.status === 1 ? 'Active' : 'Inactive',
            `"${getBranchName(stock.branch_id, branchNames).replace(/"/g, '""')}"`,
            `"${(stock.description || '').replace(/"/g, '""')}"`,
            `"${(stock.notes || '').replace(/"/g, '""')}"`,
            new Date(stock.created_at).toLocaleString(),
            new Date(stock.updated_at).toLocaleString()
        ]);

        // Create CSV content
        const csvContent = [
            headers.join(','),
            ...csvData.map(row => row.join(','))
        ].join('\n');

        // Create and download without FileSaver
        const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `stocks_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);

        return { success: true };
    } catch (error) {
        console.error('Stocks CSV export failed:', error);
        throw new Error('Failed to export to CSV: ' + error.message);
    }
};

// Export stock history to PDF
export const exportStockHistoryPDF = (stock, history, userNames) => {
    try {
        const doc = new jsPDF('landscape', 'mm', 'a4');
        
        // Set document properties
        doc.setProperties({
            title: 'Stock History Report - ' + stock.name,
            subject: 'Stock Adjustment History',
            author: companyInfo.name,
            keywords: 'stock, history, adjustment, report',
            creator: companyInfo.name
        });
        
        // Title section
        doc.setFontSize(20);
        doc.setTextColor(25, 118, 210);
        doc.text('STOCK ADJUSTMENT HISTORY REPORT', 148, 20, { align: 'center' });
        
        doc.setFontSize(12);
        doc.setTextColor(80, 80, 80);
        doc.text(`For: ${stock.name}`, 148, 30, { align: 'center' });
        
        // Generation details
        doc.setFontSize(10);
        doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, 40);
        doc.text(`Total Adjustments: ${history.length}`, 14, 47);
        
        // Stock Information Section
        doc.setFontSize(11);
        doc.setTextColor(25, 118, 210);
        doc.text('STOCK INFORMATION', 14, 60);
        
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        
        let yPos = 68;
        const infoLines = [
            `Item Name: ${stock.name}`,
            `Current Balance: ${formatNumberWithComma(stock.balance)} ${stock.uom}`,
            `Unit of Measure: ${stock.uom}`,
            `Cost Price: KES ${formatNumberWithComma(stock.cost, 2)}`,
            `Selling Price: KES ${formatNumberWithComma(stock.selling, 2)}`,
            `Stock Level: ${getStockLevel(stock.balance)}`,
            `Status: ${stock.status === 1 ? 'Active' : 'Inactive'}`
        ];
        
        infoLines.forEach(line => {
            doc.text(line, 14, yPos);
            yPos += 6;
        });
        
        // History Table Header
        yPos = 95;
        doc.setFontSize(11);
        doc.setTextColor(25, 118, 210);
        doc.text('ADJUSTMENT HISTORY', 14, yPos);
        yPos += 10;
        
        // Draw table
        yPos = drawHistoryTable(doc, stock, history, userNames, yPos);
        
        // Add footer
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(`Page ${i} of ${pageCount}`, 148, 205, { align: 'center' });
            doc.text(`Generated by ${companyInfo.name}`, 148, 210, { align: 'center' });
        }
        
        const fileName = `stock_history_${stock.name}_${new Date().toISOString().split('T')[0]}.pdf`;
        doc.save(fileName);
        
        return { success: true, fileName };
    } catch (error) {
        console.error('Stock history PDF export failed:', error);
        throw new Error('Failed to export stock history PDF: ' + error.message);
    }
};

// Helper function to draw history table
const drawHistoryTable = (doc, stock, history, userNames, startY) => {
    const pageWidth = doc.internal.pageSize.width;
    const margin = 14;
    const rowHeight = 12;
    
    // Column widths for landscape
    const colWidths = [15, 30, 35, 40, 25, 25, 25, 40];
    const headers = ['#', 'Date', 'Action Type', 'User', 'Old Qty', 'Change', 'New Qty', 'Reason'];
    
    let currentY = startY;
    const maxRowsPerPage = Math.floor((doc.internal.pageSize.height - currentY - 30) / rowHeight);
    let processedRows = 0;
    let page = 1;
    
    while (processedRows < history.length) {
        if (page > 1) {
            doc.addPage('landscape');
            currentY = margin;
            
            // Draw header on new page
            doc.setFontSize(11);
            doc.setTextColor(25, 118, 210);
            doc.text('ADJUSTMENT HISTORY (Continued)', 14, currentY);
            currentY += 10;
        }
        
        // Draw table header
        doc.setFillColor(52, 152, 219);
        doc.setTextColor(255, 255, 255);
        doc.setFont(undefined, 'bold');
        doc.setFontSize(9);
        
        let xPos = margin;
        doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
        
        headers.forEach((header, index) => {
            doc.text(header, xPos + 2, currentY + 8);
            xPos += colWidths[index];
        });
        
        doc.setDrawColor(52, 152, 219);
        doc.setLineWidth(0.5);
        doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
        
        currentY += rowHeight;
        
        // Draw table rows
        doc.setFont(undefined, 'normal');
        doc.setTextColor(0, 0, 0);
        doc.setFontSize(8);
        
        const rowsToProcess = Math.min(maxRowsPerPage, history.length - processedRows);
        
        for (let i = 0; i < rowsToProcess; i++) {
            const historyItem = history[processedRows + i];
            const rowIndex = processedRows + i;
            
            // Alternate row colors
            if (rowIndex % 2 === 0) {
                doc.setFillColor(248, 248, 248);
            } else {
                doc.setFillColor(255, 255, 255);
            }
            
            // Draw row background
            doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
            
            // Row data
            const rowData = [
                (rowIndex + 1).toString(),
                new Date(historyItem.created_at).toLocaleDateString(),
                historyItem.action_type.charAt(0).toUpperCase() + historyItem.action_type.slice(1),
                getCategoriesNameById(historyItem.user_id, userNames),
                formatNumberWithComma(historyItem.old_quantity),
                (historyItem.quantity_change >= 0 ? '+' : '') + formatNumberWithComma(historyItem.quantity_change),
                formatNumberWithComma(historyItem.new_quantity),
                historyItem.reason || 'N/A'
            ];
            
            xPos = margin;
            rowData.forEach((cell, cellIndex) => {
                // Set color for change column
                if (cellIndex === 5) {
                    doc.setTextColor(historyItem.quantity_change >= 0 ? 39 : 231);
                } else {
                    doc.setTextColor(0, 0, 0);
                }
                
                // Truncate long text
                let displayText = cell;
                if (cellIndex === 7 && doc.getTextWidth(cell) > colWidths[cellIndex]) {
                    displayText = cell.substring(0, 20) + '...';
                }
                
                doc.text(displayText, xPos + 2, currentY + 8);
                xPos += colWidths[cellIndex];
            });
            
            // Draw row separator
            doc.setDrawColor(200, 200, 200);
            doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
            
            currentY += rowHeight;
        }
        
        processedRows += rowsToProcess;
        page++;
        
        if (processedRows < history.length) {
            currentY = margin;
        }
    }
    
    return currentY;
};

// Function declared in a loop contains unsafe references to variable(s) 'xPos', 'currentY', 'xPos'.eslintno-loop-func